import { cloneElement, useEffect, useRef, useState } from "react";
import { callApiWithAuthorization } from "../../API/CallApi";
import { createEntityConfigurations, deleteEntity, getEntities, getEntityById, updateEntity, updateEntityAndConfiguration } from "../../API/Entity/Entity";
import { createAttribute, deleteAttribute, getAttributes, updateAttribute, updateAttributeFormula } from "../../API/Attribute/Attribute";
import { extractPropertiesFromTree, getActionById, getActionParentById, getComponentById, getComponentsByType, requestInformationFromAppModel, transformPropertiesToAttributeFormat, transformPropertiesToAttributeFormatForUpdate, transformPropertiesToRoleFormat, transformToCreateEntityDatabaseFormat, transformToPortalFormat } from "./utils";
import { createEntityRelation, createReferenceAttribute, deleteEntityRelation, updateEntityRelation } from "../../API/EntityRelations/EntityRelations";
import { getAllAttributes, getProjectRelations } from "../Helpers/Schema/Schema";
import { createRole, deleteRole, getProjectRoles, updateAttributesBasedonEntityRight, updatePagesBasedonEntityRight, updateRole, updateSpecificPageBasedonEntityRight } from "../../API/Role/Role";
import { createPortal, deletePortal, getPortals, reorderMenuItems, updateItem, updatePortal, updatePortalTargetPage } from "../../API/Portal/Portal";
import { generateDateTimeFormula, generateNumberFormula, generateStringFormula } from "../Attributes/FormulaDialog/utils";
import Markdown from "react-markdown";
import { createComponent, getEntityPages, getPage, updateComponent, updatePage } from "../../API/Display/Display";
import { generateIncrementalChanges, removeAllChanges, validateForPublish } from "../../API/Generation/Generation";
import { getInstances, publishApplication, updateProjectPortalConfig } from "../Helpers/Launch";
import { getProject, updateProjectPropertiesAfterPublish } from "../../API/Project/project";
import { useMsal } from "@azure/msal-react";
import { postMessageToRuntime } from "../Helpers/Runtime/runtime";
import { getLoggedInUserUsingAccessToken } from "../../API/User/User";
import { Breadcrumb } from "react-bootstrap";
import { DropDownList, DropDownTree } from "@progress/kendo-react-dropdowns";
import { groupBy } from "@progress/kendo-data-query";
import { Dialog } from "@progress/kendo-react-dialogs";
import { validateAttributeChange } from "./validations/attribute.validation";
import { getEntitiesShownInMenuItems } from "../Helpers/Portal/Portal";
import Scrollable from "../Shared/Edit/Scrollable";
import { getComponentByProperty } from "../Display/Components/utils";
import Loader from './Loader';

function uuidv4() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        var r = (Math.random() * 16) | 0,
            v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * @param {Object} param0 props
 * @param {Object} param0.context format - { entity?, attribute?, relation?, role?, portal?, page?, component? } - used to set initial context so this component can be used anywhere
 * @returns 
 */
function RealtimeAPIChatbox({ organizationId, projectId, subscriptionId, entities, entityId, pageSysName, context, instanceId }) {
    const [isSessionActive, setIsSessionActive] = useState(false);
    const [events, setEvents] = useState([]);
    const [dataChannel, setDataChannel] = useState(null);
    const [message, setMessage] = useState("");
    const [messages, setMessages] = useState([]);
    const peerConnection = useRef(null);
    const audioElement = useRef(null);
    const entityAttributeMapRef = useRef({});
    const entityNameIdMapRef = useRef({});
    const entitySysNameToNameMapRef = useRef({});
    const formulaThreadIdRef = useRef(null);
    const msalContext = useMsal();

    const [allEntities, setAllEntities] = useState([]);

    const [treeOptions, setTreeOptions] = useState([
        {
            id: 1,
            name: "Entities",
            isExpanded: false
        },
        {
            id: 2,
            name: "Entity Relations",
            isExpanded: false
        },
        {
            id: 3,
            name: "Roles",
            isExpanded: false
        },
        {
            id: 4,
            name: "Portals",
            isExpanded: false
        }
    ]);
    const [treeValue, setTreeValue] = useState(null);

    const [subTreeOptions, setSubTreeOptions] = useState([]);
    const [subTreeValue, setSubTreeValue] = useState(null);

    useEffect(() => {
        getSession()
            .then(async (response) => {
                const EPHEMERAL_KEY = response.client_secret.value;
                const pc = new RTCPeerConnection();

                // Set up to play remote audio from the model
                audioElement.current = document.createElement("audio");
                audioElement.current.autoplay = true;
                pc.ontrack = (e) => (audioElement.current.srcObject = e.streams[0]);

                // Add local audio track for microphone input in the browser
                // const ms = await navigator.mediaDevices.getUserMedia({
                //     audio: true,
                // });
                // pc.addTrack(ms.getTracks()[0]);
                pc.addTransceiver('audio', { direction: 'recvonly' });
                pc.addTransceiver('video', { direction: 'recvonly' });

                // Set up data channel for sending and receiving events
                const dc = pc.createDataChannel("oai-events");
                setDataChannel(dc);

                // Start the session using the Session Description Protocol (SDP)
                const offer = await pc.createOffer();
                await pc.setLocalDescription(offer);

                const baseUrl = "https://api.openai.com/v1/realtime";
                const model = "gpt-4o-realtime-preview-2024-12-17";
                const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
                    method: "POST",
                    body: offer.sdp,
                    headers: {
                        Authorization: `Bearer ${EPHEMERAL_KEY}`,
                        "Content-Type": "application/sdp",
                    },
                });

                const answer = {
                    type: "answer",
                    sdp: await sdpResponse.text(),
                };
                await pc.setRemoteDescription(answer);

                peerConnection.current = pc;
            });

        getTreeOptionsData()

        return () => {
            if (peerConnection.current) {
                peerConnection.current.close();
            }
            if (dataChannel) {
                dataChannel.close();
            }
        }
    }, [])

    useEffect(() => {
        setAllEntities(entities ?? []);
    }, [entities])

    async function getTreeOptionsData() {
        const entities = await getEntities(projectId);
        const entityRelations = await getProjectRelations(projectId);
        const roles = await getProjectRoles(projectId);
        const portals = await getPortals(projectId);

        const treeOptions = [
            {
                id: 1,
                name: "Entities",
                isExpanded: false,
                children: entities.map(entity => ({
                    id: entity._id,
                    name: entity.name,
                    type: "entity"
                }))
            },
            {
                id: 2,
                name: "Entity Relations",
                isExpanded: false,
                children: entityRelations.map(relation => ({
                    id: relation._id,
                    name: relation.identifier,
                    type: "relation"
                }))
            },
            {
                id: 3,
                name: "Roles",
                isExpanded: false,
                children: roles.map(role => ({
                    id: role._id,
                    name: role.roleName,
                    type: "role"
                }))
            },
            {
                id: 4,
                name: "Portals",
                isExpanded: false,
                children: portals.map(portal => ({
                    id: portal._id,
                    name: portal.portalName,
                    type: "portal"
                }))
            }
        ]
        setTreeOptions(treeOptions);

        if (entityId) {
            const entity = entities.find(entity => entity._id === entityId);
            const attributes = await getAttributes(entity._id);
            const pages = await getEntityPages(entity._id);
            setSubTreeOptions([
                {
                    id: 1,
                    name: "Attributes",
                    isExpanded: false,
                    children: attributes.map(attribute => ({
                        id: attribute._id,
                        name: attribute.attributeName
                    }))
                },
                {
                    id: 2,
                    name: "Pages",
                    isExpanded: false,
                    children: pages.data.map(page => ({
                        id: page._id,
                        name: page.pageName
                    }))
                }
            ])
            setTreeValue({
                id: entity._id,
                name: entity.name
            });
            setSubTreeValue({
                id: pages.data[0]._id,
                name: pages.data[0].pageName
            })
        }
    }

    useEffect(() => {
        if (treeOptions && treeOptions[0] && treeOptions[0].children && treeOptions[0].children.length > 0) {
            const entity = treeOptions[0].children.find(entity => entity.id === entityId);
            handleChange({
                value: {
                    id: entity._id,
                    name: entity.name
                }
            })
        }
    }, [entityId])

    async function initContext() {
        const entities = await getEntities(projectId);
        entities.forEach(entity => {
            entityNameIdMapRef.current[entity.sysName] = entity._id;
            entitySysNameToNameMapRef.current[entity.sysName] = entity.name;
        });
        const entity = entities.find(entity => entity._id === entityId);
        const attributes = await getAttributes(entityId);
        entityAttributeMapRef.current[entityId] = attributes;
        const pages = await getEntityPages(entityId);
        let message = `
            When user refers to 'this' in a message, try bubbling up the context from current page.
            An action in current page may refer to the following: Update property of a component, add attribute to a component, remove attribute from a component, set attributes of a component, toggle action of a component, etc.
            If the 'this' cannot be resolved to a page, then try resolving it to an entity.
            An action in current entity may refer to the following: Create an attribute, update value of an attribute, delete an attribute, create a relation, update a relation, delete a relation, update a formula, delete a formula, create state rules, publish project.
            If the 'this' cannot be resolved to an entity, then try resolving it to a project.
            An action in current project may refer to the following: Create an entity, get all entities, update an entity, delete an entity, create a role, get all roles, update a role, delete a role, create a portal, get all portals, update a portal, delete a portal.
            If the 'this' cannot be resolved to a project, then ask the user to provide more context.
        `;
        sendClientEvent({
            type: "conversation.item.create",
            item: {
                type: "message",
                role: "system",
                content: [
                    {
                        type: "input_text",
                        text: message.trim(),
                    },
                ],
            },
        });
        if (context) {
            if (context.page && context.component) {
                const targetPage = pages.data.find(page => page._id === context.page._id);
                const { components, ...properties } = targetPage;
                const selectedComponent = getComponentByProperty(components, "id", context.component.id);
                const message = `
                    The user has navigated to the page ${targetPage.pageName}.
                    The details of this current page are: ${JSON.stringify(properties)}
                    The user has selected the component ${selectedComponent.name}.
                    The details of this component are: ${JSON.stringify(selectedComponent)}
                    STRICTLY FOLLOW THE CONTEXT PROVIDED BY THE USER.
                    YOU DO NOT NEED TO DO ANY OPERATIONS OTHER THAT THOSE SUPPORTED BY THE COMPONENT.
                    IN THIS CASE, YOU SHOULD ONLY ALLOW USER TO DO THE FOLLOWING:
                        - UPDATE COMPONENT PROPERTIES
                        ${selectedComponent.attributes && "- UPDATE COMPONENT ATTRIBUTES\n"}
                        ${selectedComponent.actions && "- UPDATE COMPONENT ACTIONS\n"}
                        ${selectedComponent.steps && "- UPDATE COMPONENT LAYOUT\n"}
                        ${selectedComponent.children && selectedComponent.componentType === "Tab Container" && "- UPDATE COMPONENT CHILDREN\n"}
                        - CREATE NEW ATTRIBUTES OF ENTITY
                        - UPDATE EXISTING ATTRIBUTES OF ENTITY
                        - DELETE ATTRIBUTES OF ENTITY
                    IF THE USER ASKS TO DO ANYTHING ELSE, THEN PLEASE SAY NO.
                `;
                sendClientEvent({
                    type: "conversation.item.create",
                    item: {
                        type: "message",
                        role: "system",
                        content: [
                            {
                                type: "input_text",
                                text: message,
                            },
                        ],
                    },
                });
                sendClientEvent({
                    type: "response.create"
                })
            }
        } else {
            let message = `
                This is the current entity.
                The details of this entity are: ${JSON.stringify(entity)}
                This entity has ${attributes.length} attributes.
            `.trim();
            if (pageSysName) {
                const targetPage = pages.data.find(page => page.sysPageName === pageSysName);
                const { components, ...properties } = targetPage;
                if (targetPage.subPageType === "Single Record") {
                    message = `
                    DO NOT SERVE ANY RESPONSE TO THE USER RELATED TO ANY PAGE OR COMPONENT.
                    IF USER ASKS FOR SOMETHING, SAY WORKING ON SINGLE RECORD PAGE USING PROMPTS IS STILL IN DEVELOPMENT.
                    SERVER USER WITH ANY SCHEMA RELATED QUERIES. FOR INSTANCE, CRUD ENTITIES, ATTRIBUTES, RELATIONS, ROLES, PORTALS.
                    `
                } else {
                    message += `
                        This is the current page ${targetPage.pageName}.
                        The details of this current page are: ${JSON.stringify(properties)}
                    `;
                }
            }
            sendClientEvent({
                type: "conversation.item.create",
                item: {
                    type: "message",
                    role: "system",
                    content: [
                        {
                            type: "input_text",
                            text: message.trim(),
                        },
                    ],
                },
            });
        }
    }

    // Attach event listeners to the data channel when a new one is created
    useEffect(() => {
        if (dataChannel) {

            // Append new server events to the listfaafaf
            dataChannel.addEventListener("message", async (e) => {
                const data = JSON.parse(e.data);
                if (data.type === "response.text.done") {
                    // setMessages((prev) => [...prev, {
                    //     role: "model",
                    //     content: data.text
                    // }]);
                    debugger;
                    setMessages(prev => [
                        ...prev.filter(message => message.role !== "loader"),
                        {
                            role: "model",
                            content: data.text
                        }
                    ]);
                } else if (data.type === "response.done" || data.type === "function_call") {
                    if (data.response.output && data.response.output.length > 0) {
                        const hasToolCalls = data.response.output.some(output => output.type === "function_call");
                        if (hasToolCalls) {
                            setMessages(prev => [
                                ...prev.filter(message => message.role !== "loader"),
                                { role: "loader" }
                            ])
                        }
                        handleToolCalls(data.response.output, data.response.metadata?.eventId);
                    }
                }
            });

            // Set session active when the data channel is opened
            dataChannel.addEventListener("open", () => {
                initContext()
                window.addEventListener('message', handleMessage);
                requestInformationFromAppModel(organizationId, projectId, instanceId);
                setIsSessionActive(true);
                setEvents([]);
            });

            dataChannel.addEventListener("close", () => {
                window.removeEventListener('message', handleMessage);
            });

            dataChannel.addEventListener("error", (e) => {
                console.error("Data channel error", e);
                console.log(e.stack);
            });

            return () => {
                window.removeEventListener('message', handleMessage);
            }
        }
    }, [dataChannel]);

    function handleMessage(event) {
        try {
            if (event.origin === window.location.origin) {
                const json = typeof event.data === "string" ? JSON.parse(event.data) : undefined;
                if (json) {
                    switch (json.type) {
                        case "entitySelected": {
                            const entity = json.data;
                            let message = `
                                The user has changed the current entity.
                                Now 'this' refers to the current entity.
                                Entity name: ${entity.name}
                                Entity id: ${entity._id}
                            `.trim();
                            sendClientEvent({
                                type: "conversation.item.create",
                                item: {
                                    type: "message",
                                    role: "system",
                                    content: [
                                        {
                                            type: "input_text",
                                            text: message,
                                        },
                                    ],
                                },
                            });
                            setMessages((prev) => [...prev, {
                                role: "info",
                                content: entity.name + " is now the current entity."
                            }]);
                            break;
                        }
                        case "entityEntered": {
                            const entity = json.data;
                            let message = `
                                The user has entered the current entity.
                                Entity name: ${entity.name}
                                Entity id: ${entity._id}
                            `.trim();
                            sendClientEvent({
                                type: "conversation.item.create",
                                item: {
                                    type: "message",
                                    role: "system",
                                    content: [
                                        {
                                            type: "input_text",
                                            text: message,
                                        },
                                    ],
                                },
                            });
                            break;
                        }
                        case "entityAttributeSelected": {
                            const attribute = json.data;
                            let message = `
                                The user has selected the current attribute.
                                Attribute name: ${attribute.attributeName}
                                Attribute id: ${attribute._id}
                            `.trim();
                            sendClientEvent({
                                type: "conversation.item.create",
                                item: {
                                    type: "message",
                                    role: "system",
                                    content: [
                                        {
                                            type: "input_text",
                                            text: message,
                                        },
                                    ],
                                },
                            });
                            break;
                        }
                        case "entityRelationSelected": {
                            const relation = json.data;
                            let message = `
                                The user has selected the current relation.
                                Relation identifier: ${relation.identifier}
                                Relation id: ${relation._id}
                            `.trim();
                            sendClientEvent({
                                type: "conversation.item.create",
                                item: {
                                    type: "message",
                                    role: "system",
                                    content: [
                                        {
                                            type: "input_text",
                                            text: message,
                                        },
                                    ],
                                },
                            });
                            break;
                        }
                        case "roleSelected": {
                            const role = json.data;
                            let message = `
                                The user has selected the current role.
                                Role name: ${role.roleName}
                                Role id: ${role._id}
                            `.trim();
                            sendClientEvent({
                                type: "conversation.item.create",
                                item: {
                                    type: "message",
                                    role: "system",
                                    content: [
                                        {
                                            type: "input_text",
                                            text: message,
                                        },
                                    ],
                                },
                            });
                            break;
                        }
                        case "portalSelected": {
                            const portal = json.data;
                            let message = `
                                The user has selected this portal, now this is the current portal.
                                Portal name: ${portal.portalName}
                                Portal id: ${portal._id}
                            `.trim();
                            sendClientEvent({
                                type: "conversation.item.create",
                                item: {
                                    type: "message",
                                    role: "system",
                                    content: [
                                        {
                                            type: "input_text",
                                            text: message,
                                        },
                                    ],
                                },
                            });
                            break;
                        }
                        case "changeContext": {
                            changeContext(json.data);
                            break;
                        }
                        default:
                            break;
                    }
                }
            } else if (event.origin === "http://localhost:4200") {
                if (typeof event.data === "string") {
                    const data = JSON.parse(event.data);
                    if (data.message === "iframeParams") {
                        handleRuntimePageChange(data.queryParams.entitySysName, data.queryParams.pageSysName);
                    }
                }
            }
        } catch (e) {
            console.error(e);
        }
    }

    async function handleRuntimePageChange(entitySysName, pageSysName) {
        const entities = await getEntities(projectId);
        debugger;
        if (entities && entities.length > 0) {
            const entity = entities.find(entity => entity.sysName === entitySysName);
            if (entity) {
                const pages = await getEntityPages(entity._id);
                if (pages && pages.data && pages.data.length > 0) {
                    debugger;
                    const page = pages.data.find(page => page.sysPageName === pageSysName);
                    if (page && page._id) {
                        let message = "";
                        const { components, ...properties } = page;
                        if (page.subPageType === "Single Record") {
                            message = `
                            DO NOT SERVE ANY RESPONSE TO THE USER RELATED TO ANY PAGE OR COMPONENT.
                            IF USER ASKS FOR SOMETHING, SAY WORKING ON SINGLE RECORD PAGE USING PROMPTS IS STILL IN DEVELOPMENT.
                            `
                        } else {
                            message = `
                                The user has navigate to the entity ${entity.name}. The id of the entity is: ${entity._id}
                                The user has navigated to the page ${page.pageName}. The id of the page is: ${page._id}
                                The current page thus becomes ${page.pageName}. And the current entity becomes ${entity.name}.
                            `;
                        }
                        sendClientEvent({
                            type: "conversation.item.create",
                            item: {
                                type: "message",
                                role: "system",
                                content: [
                                    {
                                        type: "input_text",
                                        text: message,
                                    },
                                ],
                            },
                        });
                        sendClientEvent({ type: "response.create" });
                        setTreeValue({
                            id: entity._id,
                            name: entity.name
                        })
                        // setTreeValue()
                        setSubTreeValue({
                            id: page._id,
                            name: page.pageName
                        })
                        setMessages((prev) => [...prev, {
                            role: "info",
                            content: entity.name + "'s " + page.pageName + " is now the current page."
                        }]);
                    } else {
                        const message = `
                            The user has navigated to the entity ${entity.name}. The id of the entity is: ${entity._id}
                            The user has navigated to a page which does not exist.
                            The current entity becomes ${entity.name} but the page selected cannot be found.
                        `;
                        sendClientEvent({
                            type: "conversation.item.create",
                            item: {
                                type: "message",
                                role: "system",
                                content: [
                                    {
                                        type: "input_text",
                                        text: message,
                                    },
                                ],
                            },
                        });
                        sendClientEvent({ type: "response.create" });
                        setTreeValue({
                            id: entity._id,
                            name: entity.name
                        })
                        setMessages((prev) => [...prev, {
                            role: "info",
                            content: entity.name + " is now the current entity. The page selected cannot be found."
                        }]);
                    }
                } else {
                    const message = `
                        The user has navigated to the entity ${entity.name}. The id of the entity is: ${entity._id}
                        The user has navigated to an entity which does not have any pages.
                        The current entity becomes ${entity.name} but the entity does not have any pages.
                    `;
                    sendClientEvent({
                        type: "conversation.item.create",
                        item: {
                            type: "message",
                            role: "system",
                            content: [
                                {
                                    type: "input_text",
                                    text: message,
                                },
                            ],
                        },
                    });
                    sendClientEvent({ type: "response.create" });
                    // handleChange({
                    //     value: {
                    //         id: entity._id,
                    //         name: entity.name
                    //     }
                    // })
                    setTreeValue({
                        id: entity._id,
                        name: entity.name
                    })
                    setMessages((prev) => [...prev, {
                        role: "info",
                        content: entity.name + " is now the current entity. The entity does not have any pages."
                    }]);
                }
            } else {
                const message = `
                    The user has navigated to an entity which does not exist.
                `;
                sendClientEvent({
                    type: "conversation.item.create",
                    item: {
                        type: "message",
                        role: "system",
                        content: [
                            {
                                type: "input_text",
                                text: message,
                            },
                        ],
                    },
                });
                sendClientEvent({ type: "response.create" });
                setMessages((prev) => [...prev, {
                    role: "info",
                    content: "The entity selected cannot be found."
                }]);
            }
        } else {
            const message = `
                The user has navigated to an entity which does not exist.
            `;
            sendClientEvent({
                type: "conversation.item.create",
                item: {
                    type: "message",
                    role: "system",
                    content: [
                        {
                            type: "input_text",
                            text: message,
                        },
                    ],
                },
            });
            sendClientEvent({ type: "response.create" });
            setMessages((prev) => [...prev, {
                role: "info",
                content: "No entities found in the project."
            }]);
        }
    }

    async function getSession() {
        const request = await callApiWithAuthorization('/api/v0/test-realtime/token');
        const response = await request.json();
        return response;
    }

    // Send a message to the model
    function sendClientEvent(message, metadata) {
        if (dataChannel) {
            message.event_id = message.event_id || crypto.randomUUID();
            if (metadata) {
                message.response = { metadata };
            }
            dataChannel.send(JSON.stringify(message));
            setEvents((prev) => [message, ...prev]);
            return message.event_id;
        } else {
            console.error(
                "Failed to send message - no data channel available",
                message,
            );
        }
    }

    // Send a text message to the model
    function sendTextMessage() {
        const event = {
            type: "conversation.item.create",
            item: {
                type: "message",
                role: "user",
                content: [
                    {
                        type: "input_text",
                        text: message,
                    },
                ],
            },
        };

        const eventId = sendClientEvent(event);
        sendClientEvent({ type: "response.create" }, { eventId });
        setMessages((prev) => [...prev, {
            role: "user",
            content: message,
            eventId: eventId
        }]);
    }

    async function handleToolCalls(toolCalls, eventId) {
        const toolCallResponses = [];
        for (const response of toolCalls) {
            if (response.type === "function_call") {
                const toolCallResponse = await executeToolCall(response.name, JSON.parse(response.arguments));
                toolCallResponses.push({
                    "type": "conversation.item.create",
                    "item": {
                        "type": "function_call_output",
                        "call_id": response.call_id,
                        "output": JSON.stringify(toolCallResponse)
                    }
                });
            }
        }
        if (toolCallResponses.length > 0) {
            for (const toolCallResponse of toolCallResponses) {
                sendClientEvent(toolCallResponse);
            }
            sendClientEvent({ type: "response.create" }, { eventId });
        }
        if (eventId && toolCallResponses.length > 0) {
            setMessages(messages => {
                try {
                    return messages.map(message => {
                        if (message.eventId === eventId) {
                            const messageToolCalls = message.toolCalls ? [...message.toolCalls, ...toolCalls] : toolCalls;
                            return {
                                ...message,
                                toolCalls: messageToolCalls
                            }
                        }
                        return message;
                    })
                } catch (e) {
                    console.error(e);
                    console.log(e.stack);
                    return messages;
                }
            })
        }
    }

    async function executeToolCall(toolCallName, properties) {
        try {
            switch (toolCallName) {
                case "createEntity":
                    const entity = await createEntityConfigurations(projectId, {
                        subscriptionId: subscriptionId,
                        properties: transformToCreateEntityDatabaseFormat(properties)
                    });
                    setAllEntities((prev) => [...prev, entity]);
                    return entity;
                case "getEntities":
                    if (allEntities && allEntities.length > 0) {
                        if (properties && properties.pick && properties.pick.length > 0) {
                            const allEntitiesWithProperties = allEntities.map(entity => {
                                const entityProperties = {};
                                properties.pick.forEach(property => {
                                    entityProperties[property] = entity[property];
                                });
                                return entityProperties;
                            });
                            return allEntitiesWithProperties;
                        }
                        return allEntities;
                    }
                    const entities = await getEntities(projectId);
                    entities.forEach(entity => {
                        entityNameIdMapRef.current[entity.sysName] = entity._id;
                        entitySysNameToNameMapRef.current[entity.sysName] = entity.name;
                    });
                    if (properties && properties.pick && properties.pick.length > 0) {
                        const allEntitiesWithProperties = entities.map(entity => {
                            const entityProperties = {};
                            properties.pick.forEach(property => {
                                entityProperties[property] = entity[property];
                            });
                            return entityProperties;
                        });
                        return allEntitiesWithProperties;
                    }
                    return entities;
                case "updateEntity": {
                    const { entityId, ...entityProperties } = properties;
                    entityProperties.projectId = projectId;
                    const updatedEntity = await updateEntityAndConfiguration(entityId, entityProperties);
                    setAllEntities(entities => entities.map(entity => {
                        if (entity._id === entityId) {
                            return updatedEntity;
                        }
                        return entity;
                    }))
                    return updatedEntity;
                }
                case "deleteEntity": {
                    const entities = await getEntities(projectId);
                    const entity = entities.find(entity => entity._id === properties.entityId);
                    if (entity.isUserEntity) {
                        throw new Error("User entity cannot be deleted.");
                    }
                    const deletedEntity = await deleteEntity(projectId, properties.entityId);
                    setAllEntities(entities => entities.filter(entity => entity._id !== properties.entityId));
                    return deletedEntity;
                }
                case "createAttribute": {
                    const { entityId, properties: attributeProperties } = properties;
                    const newAttributeProperties = transformPropertiesToAttributeFormat(attributeProperties);
                    let attribute;
                    if (newAttributeProperties.attributeType === "Entity Lookup") {
                        attribute = await createReferenceAttribute({
                            organizationId,
                            projectId,
                            entityRelationId: attributeProperties.referenceRelationId,
                            parentEntityId: entityId,
                            referencedEntityId: attributeProperties.referenceEntityId,
                            referencedAttributeId: attributeProperties.referenceAttributeId,
                            attributeProperties: newAttributeProperties
                        })
                    } else {
                        attribute = await createAttribute(entityId, {
                            subscriptionId,
                            projectId,
                            properties: transformPropertiesToAttributeFormat(attributeProperties)
                        });
                    }
                    return attribute;
                }
                case "updateAttribute": {
                    const { attributeId, attributeType, properties: attributeProperties } = properties;
                    let attribute = getAttributeFromMap(attributeId);
                    if (!attribute) {
                        throw new Error("Please fetch attributes of entity before updating an attribute.");
                    }
                    if (attributeType === undefined || attributeType === null) {
                        throw new Error("Attribute type is required to update an attribute.");
                    }
                    if (attribute.attributeType !== attributeType) {
                        throw new Error("Attribute type cannot be changed.");
                    }
                    attributeProperties.attributeType = attributeType;
                    const transformedAttributeProperties = transformPropertiesToAttributeFormatForUpdate(projectId, attributeProperties, attribute);
                    validateAttributeChange(transformedAttributeProperties, attribute);
                    const updatedAttribute = await updateAttribute(attributeId, transformedAttributeProperties);
                    return updatedAttribute;
                }
                case "deleteAttribute": {
                    const { entityId, attributeId } = properties;
                    const attributes = await getAttributes(entityId);
                    const attribute = attributes.find(attribute => attribute._id === attributeId);
                    if (attribute.isAzureAttribute || attribute.isSystemAttribute) {
                        throw new Error("There is a system restrcition that this attribute cannot be deleted.");
                    }
                    const deletedAttribute = await deleteAttribute(entityId, attributeId);
                    return deletedAttribute;
                }
                case "getAttributesOfEntity": {
                    const { entityId } = properties;
                    const attributes = await getAttributes(entityId);
                    entityAttributeMapRef.current[entityId] = attributes;
                    if (properties && properties.pick && properties.pick.length > 0) {
                        const allAttributesWithProperties = attributes.map(attribute => {
                            const attributeProperties = {};
                            properties.pick.forEach(property => {
                                attributeProperties[property] = attribute[property];
                            });
                            return attributeProperties;
                        });
                        return allAttributesWithProperties;
                    }
                    return attributes;
                }
                case "createEntityRelation": {
                    const sysRelationName = properties.fromEntityId.substring(0, 5) + properties.toEntityId.substring(0, 5);
                    const sysReverseRelationName = properties.toEntityId.substring(0, 5) + properties.fromEntityId.substring(0, 5);
                    const relation = await createEntityRelation(projectId, organizationId, {
                        ...properties,
                        sysRelationName,
                        sysReverseRelationName
                    });
                    return relation;
                }
                case "getProjectEntityRelations": {
                    const relations = await getProjectRelations(projectId);
                    return relations;
                }
                case "updateEntityRelation": {
                    const { relationId, properties: relationProperties } = properties;
                    await updateEntityRelation(relationId, relationProperties);
                    return "Entity relation updated successfully.";
                }
                case "deleteEntityRelation": {
                    const { relationId } = properties;
                    await deleteEntityRelation(relationId);
                    return "Entity relation deleted successfully.";
                }
                case "createRole": {
                    const role = await createRole(projectId, transformPropertiesToRoleFormat(properties.roleName));
                    return role;
                }
                case "getRoles": {
                    const roles = await getProjectRoles(projectId);
                    return roles;
                }
                case "updateRole": {
                    const { roleId, properties: roleProperties } = properties;
                    const updatedRole = await updateRole(roleId, roleProperties);
                    return updatedRole;
                }
                case "deleteRole": {
                    await deleteRole(projectId, properties.roleId);
                    return "Role deleted successfully.";
                }
                case "createPortal": {
                    const portal = await createPortal({
                        projectId,
                        organizationId,
                        subscriptionId,
                        properties: transformToPortalFormat(properties)
                    });
                    return portal;
                }
                case "getPortals": {
                    const portals = await getPortals(projectId);
                    return portals;
                }
                case "updatePortal": {
                    const { portalId, properties: portalProperties } = properties;
                    const updatedPortal = await updatePortal(portalId, transformToPortalFormat(portalProperties));
                    return updatedPortal;
                }
                case "deletePortal": {
                    const { portalId } = properties;
                    await deletePortal(portalId);
                    return "Portal deleted successfully.";
                }
                case "getPortalMenuItems": {
                    const { portalId } = properties;
                    const [itemsProperties, error] = await getEntitiesShownInMenuItems(portalId);
                    if (error) {
                        throw new Error(error);
                    }
                    const items = itemsProperties.map(({ allItemProperties, targetPage, ...itemProperties }) => itemProperties);
                    return items
                }
                case "updateMenuItem": {
                    const { portalId, menuItemId, properties: menuItemProperties } = properties;
                    if (menuItemProperties.targetPageId && !menuItemProperties.entityId) {
                        const targetPage = await getPage(menuItemProperties.targetPageId);
                        if (!targetPage) {
                            throw new Error("Target page does not exist.");
                        }
                        menuItemProperties.entityId = targetPage.entityId;
                    }
                    await updateItem(portalId, menuItemId, menuItemProperties);
                    return "Done";
                }
                case "reorderMenuItems": {
                    const { portalId, items } = properties;
                    await reorderMenuItems(portalId, items);
                    return "Done";
                }
                case "createStateRules": {
                    const { attributeId, rules } = properties;
                    const roles = await getProjectRoles(projectId);
                    for (const rule of rules) {
                        if (!rule.roleIds || rule.roleIds.length === 0) {
                            throw new Error("Role ids are required for state rule.");
                        }
                        for (const roleId of rule.roleIds) {
                            if (!roles.find(role => role._id === roleId)) {
                                throw new Error("Role with id " + roleId + " does not exist.");
                            }
                        }
                    }
                    let attributeProperties = getAttributeFromMap(attributeId);
                    if (!attributeProperties) {
                        throw new Error("Please fetch attributes of entity before updating an attribute.");
                    }
                    for (const rule of rules) {
                        const fromState = attributeProperties.statefield.find(state => state._id === rule.fromStateId);
                        const toState = attributeProperties.statefield.find(state => state._id === rule.toStateId);
                        if (!fromState) {
                            throw new Error("State with id " + rule.fromStateId + " does not exist.");
                        }
                        if (!toState) {
                            throw new Error("State with id " + rule.toStateId + " does not exist.");
                        }
                    }
                    await updateAttribute(attributeId, { rules, setStatesRules: true });
                    return "Done.";
                }
                case "getPagesByEntityId": {
                    const { entityId, portalId, pick } = properties;
                    const pages = await getEntityPages(entityId, portalId);
                    // This is done so openai is not overloaded with components data of all the pages.
                    const propertiesOfPages = pages.data.map(page => {
                        if (pick && pick.length > 0) {
                            const properties = {};
                            pick.forEach(property => {
                                properties[property] = page[property];
                            });
                            return properties;
                        }
                        const { _id, pageName, subPageType, pageType, order, useAsReference } = page;
                        return {
                            _id,
                            pageName,
                            subPageType,
                            pageType,
                            order,
                            useAsReference
                        }
                    })
                    return propertiesOfPages;
                }
                case "getComponentsByPageId": {
                    const { pageId, pick } = properties;
                    const page = await getPage(pageId);
                    if (pick && pick.length > 0) {
                        return extractPropertiesFromTree(page.components, pick);
                    }
                    return page.components;
                }
                case "getComponentByPageIdAndComponentId": {
                    const { pageId, componentId } = properties;
                    const page = await getPage(pageId);
                    const component = getComponentById(page.components, componentId);
                    return component;
                }
                case "getComponentsWithComponentTypeAndPageId": {
                    const { pageId, componentType, pick } = properties;
                    const page = await getPage(pageId);
                    const components = getComponentsByType(page.components, componentType);
                    if (pick && pick.length > 0) {
                        return extractPropertiesFromTree(components, pick);
                    }
                    return components;
                }
                case "updateComponentProperty": {
                    const newProperties = properties.properties.reduce((acc, property) => {
                        let value = property.value;
                        if (property.type === "boolean") {
                            value = value === "true";
                        } else if (property.type === "number") {
                            value = Number(value);
                        } else if (property.type === "object") {
                            value = JSON.parse(value);
                        }
                        acc[property.name] = value;
                        return acc;
                    }, {});
                    const response = await updateComponent(properties.pageId, properties.componentId, newProperties);
                    if (response.status === "Failure") {
                        throw new Error(response.errors);
                    }
                    return "Done";
                }
                case "addAttributeToComponentAttributes": {
                    const { attributes, pageId, componentId } = properties;
                    const page = await getPage(pageId);
                    const component = getComponentById(page.components, componentId);
                    if (!component) {
                        throw new Error("Component with id " + componentId + " does not exist.");
                    }
                    const attributeExists = component.attributes.some(attribute => attributes.some(attr => attr.attributeId === attribute.attributeId));
                    if (attributeExists) {
                        throw new Error("Attribute already added in the component.");
                    }
                    component.attributes = component.attributes.concat(attributes.map((attribute, index) => {
                        return {
                            id: uuidv4(),
                            attributeId: attribute.attributeId,
                            hyperlink: attribute.hyperlink ?? false,
                            tooltip: true,
                            targetPageId: "",
                            relationType: "Displays",
                            entityId: attribute.entityId,
                            referencedEntityId: "",
                            attributeType: attribute.attributeType,
                            order: String((index + 1) * 10000000000),
                            cellRules: ["Number", "Picklist String"].includes(attribute.attributeType)
                        }
                    }));
                    await updateComponent(pageId, componentId, { attributes: component.attributes });
                    return "Done";
                }
                case "setComponentAttributes": {
                    const { attributes, pageId, componentId } = properties;
                    const page = await getPage(pageId);
                    const component = getComponentById(page.components, componentId);
                    if (!component) {
                        throw new Error("Component with id " + componentId + " does not exist.");
                    }
                    component.attributes = attributes.map((attribute, index) => {
                        return {
                            id: uuidv4(),
                            attributeId: attribute.attributeId,
                            hyperlink: attribute.hyperlink ?? false,
                            tooltip: true,
                            targetPageId: attribute.targetPageId ?? "",
                            relationType: "Displays",
                            entityId: attribute.entityId,
                            referencedEntityId: "",
                            attributeType: attribute.attributeType,
                            order: String((index + 1) * 10000000000),
                            cellRules: ["Number", "Picklist String"].includes(attribute.attributeType)
                        }
                    });
                    await updateComponent(pageId, componentId, { attributes: component.attributes });
                    return "Done";
                }
                case "handleComponentLayout": {
                    const { pageId, componentId, userPrompt } = properties;
                    const request = await callApiWithAuthorization('/api/v0/ai/handle-form-builder-prompt', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            pageId,
                            componentId,
                            userPrompt
                        })
                    });
                    const response = await request.json();
                    return response;
                }
                case "updateAttributeInComponentAttributes": {
                    const { attributeId, pageId, componentId, hyperlink, targetPageId, showColorFormatting } = properties;
                    const page = await getPage(pageId);
                    const component = getComponentById(page.components, componentId);
                    if (!component) {
                        throw new Error("Component with id " + componentId + " does not exist.");
                    }
                    const attribute = component.attributes.find(attribute => attribute.attributeId === attributeId);
                    if (!attribute) {
                        throw new Error("Attribute with id " + attributeId + " does not exist in the component.");
                    }
                    // Only update if the value is different
                    if (attribute.hyperlink !== hyperlink) {
                        attribute.hyperlink = hyperlink;
                    }
                    // Only update if the value is different
                    if (attribute.targetPageId !== targetPageId) {
                        attribute.targetPageId = targetPageId;
                    }
                    // Update regardless of the value
                    attribute.cellRules = showColorFormatting;
                    await updateComponent(pageId, componentId, { attributes: component.attributes });
                    return "Done";
                }
                case "removeAttributeFromComponentAttributes": {
                    const { attributeIds, pageId, componentId } = properties;
                    const page = await getPage(pageId);
                    const component = getComponentById(page.components, componentId);
                    if (!component) {
                        throw new Error("Component with id " + componentId + " does not exist.");
                    }
                    component.attributes = component.attributes.filter(attribute => !attributeIds.includes(attribute.attributeId));
                    await updateComponent(pageId, componentId, { attributes: component.attributes });
                    return "Done";
                }
                case "toggleShowAction": {
                    const { pageId, componentId, actionId } = properties;
                    const page = await getPage(pageId);
                    const component = getComponentById(page.components, componentId);
                    if (!component) {
                        throw new Error("Component with id " + componentId + " does not exist.");
                    }
                    const action = getActionById(component.actions, actionId);
                    if (!action) {
                        throw new Error("Action with id " + actionId + " does not exist.");
                    }
                    action.show = !action.show;
                    const parent = getActionParentById(component.actions, actionId);
                    if (parent) {
                        const allChildrenHidden = parent.action.every(child => !child.show);
                        if (allChildrenHidden) {
                            parent.show = false;
                        }
                    }
                    await updateComponent(pageId, componentId, { actions: component.actions });
                    return "Done";
                }
                case "addAttributeToComponentLayout": {
                    const { pageId, componentId, sectionId, stepId, attribute } = properties;
                    const page = await getPage(pageId);
                    const component = getComponentById(page.components, componentId);
                    if (!component) {
                        throw new Error("Component with id " + componentId + " does not exist.");
                    }
                    const step = component.steps.find(step => step.id === stepId);
                    if (!step) {
                        throw new Error("Step with id " + stepId + " does not exist.");
                    }
                    const section = step.layout.find(section => section.id === sectionId);
                    if (!section) {
                        throw new Error("Section with id " + sectionId + " does not exist.");
                    }
                    const ATTRIBUTE_TYPES_WITH_CELL_RULES = ["Number", "Picklist String"];

                    // Initialize segmentColor based on attribute type
                    let segmentColor = [];
                    if (attribute.attributeType === "Picklist String" && attribute.picklist) {
                        // Split picklist string by 'ɖ' separator and create color entries for each value
                        const picklistValues = attribute.picklist.split('ɖ').filter(value => value.trim() !== '');
                        segmentColor = picklistValues.map(() => ({ textColor: "#ffffff", fillColor: "#000000" }));
                    }

                    section.columns.push({
                        i: uuidv4(),
                        attributeId: attribute.attributeId,
                        attributeType: attribute.attributeType,
                        referencedEntityId: "",
                        cellRules: component.componentType === "View" && ATTRIBUTE_TYPES_WITH_CELL_RULES.includes(attribute.attributeType) ? true : false,
                        x: attribute.x,
                        y: attribute.y,
                        w: attribute.w,
                        h: generateColumnHeight(attribute.attributeType),
                        minW: generateColumnMinWidth(attribute.attributeType),
                        maxW: 12,
                        minH: generateColumnHeight(attribute.attributeType),
                        columnSettings: {
                            displayType: attribute.attributeType === "Picklist String" ? "SegmentedControl" : "Inherit",
                            fontSize: 14,
                            fontWeight: "regular",
                            placeholder: "",
                            segmentColor: segmentColor,
                            cellRules: ATTRIBUTE_TYPES_WITH_CELL_RULES.includes(attribute.attributeType) ? true : false,
                            showLabel: component.componentType === "Cards" ? true : false,
                            hyperlink: false,
                            targetPageId: ""
                        }
                    });
                    await updateComponent(pageId, componentId, { steps: component.steps });
                    return "Done";
                }
                case "setComponentLayout": {
                    const { pageId, componentId, layout } = properties;
                    const page = await getPage(pageId);
                    const component = getComponentById(page.components, componentId);
                    if (!component) {
                        throw new Error("Component with id " + componentId + " does not exist.");
                    }
                    const entityAttributes = await getAttributes(component.entityId);
                    const ATTRIBUTE_TYPES_WITH_CELL_RULES = ["Number", "Picklist String"];
                    const layoutToSave = layout.map(section => ({
                        id: uuidv4(),
                        section: section.sectionName,
                        columns: section.columns.map(column => {
                            const attribute = entityAttributes.find(attr => attr._id === column.attributeId);
                            // skip system attributes in new record page
                            // Do not go back to open ai if the attribute is a system attribute
                            // Since it is not necessary, and we can just remove it.
                            if (page.subPageType === "New Record" && attribute.isSystemAttribute) {
                                return;
                            }
                            if (attribute) {
                                // Initialize segmentColor based on attribute type
                                let segmentColor = [];
                                if (attribute.attributeType === "Picklist String" && attribute.picklist) {
                                    // Split picklist string by 'ɖ' separator and create color entries for each value
                                    const picklistValues = attribute.picklist.split('ɖ').filter(value => value.trim() !== '');
                                    segmentColor = picklistValues.map(() => ({ textColor: "#ffffff", fillColor: "#000000" }));
                                }

                                return {
                                    i: uuidv4(),
                                    attributeId: attribute._id,
                                    attributeType: attribute.attributeType,
                                    referencedEntityId: attribute.references?.entityId || undefined,
                                    cellRules: component.componentType === "View" && ATTRIBUTE_TYPES_WITH_CELL_RULES.includes(attribute.attributeType) ? true : false,
                                    x: column.x,
                                    y: column.y,
                                    w: column.w,
                                    h: generateColumnHeight(attribute.attributeType),
                                    minW: generateColumnMinWidth(attribute.attributeType),
                                    maxW: 12,
                                    minH: generateColumnHeight(attribute.attributeType),
                                    columnSettings: {
                                        displayType: attribute.attributeType === "Picklist String" ? "SegmentedControl" : "Inherit",
                                        fontSize: 14,
                                        fontWeight: "regular",
                                        placeholder: "",
                                        segmentColor: segmentColor,
                                        cellRules: ATTRIBUTE_TYPES_WITH_CELL_RULES.includes(attribute.attributeType) ? true : false,
                                        showLabel: component.componentType === "Cards" ? true : false,
                                        hyperlink: page.subPageType === "New Record" || page.subPageType === "Edit Record" ? false : column.hyperlink,
                                        targetPageId: page.subPageType === "New Record" || page.subPageType === "Edit Record" ? "" : column.targetPageId
                                    }
                                }
                            } else {
                                throw new Error("Attribute with id " + column.attributeId + " does not exist.");
                            }
                        }).filter(column => column),
                        languages: {}
                    }))
                    component.steps = [{
                        id: uuidv4(),
                        name: "Step 1",
                        layout: layoutToSave
                    }]
                    await updateComponent(pageId, componentId, { steps: component.steps });
                    return "Done";
                }
                case "updateEntityRoleRights": {
                    const { entityId, roleId, rights } = properties;
                    const entity = await getEntityById(entityId);
                    let roleRights = {};
                    if (rights === "Full access") {
                        roleRights = {
                            create: true,
                            update: true,
                            delete: true,
                            read: true
                        }
                    } else if (rights === "Read only") {
                        roleRights = {
                            create: false,
                            update: false,
                            delete: false,
                            read: true
                        }
                    } else if (rights === "No access") {
                        roleRights = {
                            create: false,
                            update: false,
                            delete: false,
                            read: false
                        }
                    } else {
                        throw new Error("Invalid rights provided.");
                    }
                    entity.roleRights = {
                        ...entity.roleRights,
                        [roleId]: {
                            ...entity.roleRights[roleId],
                            ...roleRights
                        }
                    }
                    await updateEntity(entityId, { roleRights: entity.roleRights });
                    if (rights === "Full access") {
                        await updateAttributesBasedonEntityRight(roleId, entityId, "Edit");
                        await updatePagesBasedonEntityRight(roleId, entityId, true);
                    } else if (rights === "Read only") {
                        await updateAttributesBasedonEntityRight(roleId, entityId, "Readonly");
                        await updatePagesBasedonEntityRight(roleId, entityId, true);
                        await updateSpecificPageBasedonEntityRight(roleId, entityId, false, "New Record");
                        await updateSpecificPageBasedonEntityRight(roleId, entityId, false, "Edit Record");
                    } else if (rights === "No access") {
                        await updateAttributesBasedonEntityRight(roleId, entityId, "Hidden");
                        await updatePagesBasedonEntityRight(roleId, entityId, false);
                    }
                    return "Done";
                }
                case "updateEntityAttributeRoleRights": {
                    const { entityId, attributeId, roleId, rights } = properties;
                    const entity = await getEntityById(entityId);
                    if (entity && entity.roleRights && entity.roleRights[roleId]) {
                        const isReadOnly = entity.roleRights[roleId].read && !entity.roleRights[roleId].create && !entity.roleRights[roleId].update && !entity.roleRights[roleId].delete;
                        if (isReadOnly && rights === "Edit") {
                            throw new Error("Cannot provide edit access to a read only entity.");
                        }
                        const isHidden = !entity.roleRights[roleId].read && !entity.roleRights[roleId].create && !entity.roleRights[roleId].update && !entity.roleRights[roleId].delete;
                        if (isHidden && rights !== "Hidden") {
                            throw new Error("Cannot provide access. Entity is hidden to the role.");
                        }
                    } else {
                        throw new Error("Entity does not exist");
                    }
                    const attributes = await getAttributes(entityId);
                    const attribute = attributes.find(attribute => attribute._id === attributeId);
                    attribute.roleRights = {
                        ...attribute.roleRights,
                        [roleId]: {
                            permission: rights
                        }
                    }
                    await updateAttribute(attributeId, { roleRights: attribute.roleRights });
                    return "Done";
                }
                case "updateEntityPageRoleRights": {
                    const { pageId, roleId, access } = properties;
                    const page = await getPage(pageId);
                    const entity = await getEntityById(page.entityId);
                    if (entity && entity.roleRights && entity.roleRights[roleId]) {
                        const isHidden = !entity.roleRights[roleId].read && !entity.roleRights[roleId].create && !entity.roleRights[roleId].update && !entity.roleRights[roleId].delete;
                        if (isHidden && access) {
                            throw new Error("Cannot provide access. Entity is hidden to the role.");
                        }
                        const isNewRecordOrEditRecord = page.pageType === "New Record" || page.pageType === "Edit Record";
                        const isReadOnly = entity.roleRights[roleId].read && !entity.roleRights[roleId].create && !entity.roleRights[roleId].update && !entity.roleRights[roleId].delete;
                        if (isNewRecordOrEditRecord && isReadOnly && access) {
                            throw new Error("Cannot provide access to new record and edit record. Entity is read only to the role.");
                        }
                    } else {
                        throw new Error("Entity does not exist");
                    }
                    page.roleRights = {
                        ...page.roleRights,
                        [roleId]: {
                            access: access,
                        }
                    }
                    await updatePage(pageId, { roleRights: page.roleRights });
                    return "Done";
                }
                case "updateEntityUseInPortal": {
                    const { entityId, portalId, use } = properties;
                    const entity = await getEntityById(entityId);
                    entity.configurations = {
                        ...entity.configurations,
                        [portalId]: {
                            ...entity.configurations[portalId],
                            previousUseInPortalValue: entity.configurations[portalId].useInPortal,
                            useInPortal: use
                        }
                    }
                    await updateEntity(entityId, { configurations: entity.configurations });
                    return "Done";
                }
                case "handleFormulaPrompt": {
                    const request = await callApiWithAuthorization("/api/v0/ai/handle-formula", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json"
                        },
                        body: JSON.stringify({
                            ...properties,
                            projectId,
                            threadId: formulaThreadIdRef.current
                        })
                    })
                    const response = await request.json();
                    const threadId = response.threadId;
                    const resp = response.response;
                    if (formulaThreadIdRef.current !== threadId) {
                        formulaThreadIdRef.current = threadId;
                    }
                    return resp;
                }
                case "changeContext": {
                    const response = await changeContext(properties.context);
                    return response;
                }
                case "publishProject": {
                    const resp = await performIncrementalGeneration();
                    return resp;
                }
                case "updatePortalTargetPage": {
                    const { portalId, targetPageId } = properties;
                    await updatePortalTargetPage(portalId, targetPageId);
                    return "Done";
                }
                default:
                    return "Tool call yet to be implemented.";
            }
        } catch (e) {
            console.error(e);
            console.log(e.stack);
            return {
                error: e.message
            }
        }
    }

    function generateColumnHeight(type) {
        switch (type) {
            case "Multiline Text":
                return 2
            case "Attachment":
                return 2;
            case "Html":
                return 2;
            default:
                return 1;
        }
    }

    function generateColumnWidth(type) {
        switch (type) {
            case "Multiline Text":
                return 6;
            case "Attachment":
                return 6;
            case "Html":
                return 6;
            default:
                return 6;
        }
    }

    function generateColumnMinWidth(type) {
        switch (type) {
            case "Multiline Text":
                return 6;
            case "Attachment":
                return 6;
            case "Html":
                return 6;
            default:
                return 1;
        }
    }

    async function validateApp() {
        const response = await validateForPublish(projectId, "production");
        if (response.error) {
            throw new Error(response.error);
        }
    }

    async function performIncrementalGeneration() {
        const { instances } = await getProject(projectId);
        const productionInstance = instances.find(instance => instance.instanceType === "Production");
        try {
            debugger;
            await validateApp();
            const userId = await getCurrentUserId();
            const [compressedJson, generationError] = await generateIncrementalChanges(
                projectId, organizationId, userId, productionInstance._id, false, false
            );
            if (generationError) {
                throw new Error(generationError);
            }
            await removeAllChanges(projectId);
            updateProjectPortalConfig(projectId);
            const result = await publishApplication(msalContext, productionInstance, compressedJson, false);
            if (result.status !== "Success") {
                throw new Error(result.message);
            }
            await updateProjectPropertiesAfterPublish(projectId, productionInstance._id);
            postMessageToRuntime({
                designerStatus: "Success"
            })
        } catch (e) {
            throw new Error(e.message + ". Do not attempt to republish the application again. Just show the error message to the user.");
        }
    }

    async function getCurrentUserId() {
        if (msalContext.accounts.length === 0) {
            const user = await getLoggedInUserUsingAccessToken();
            return user.azureId;
        }
        if (msalContext.accounts.length > 0)
            return msalContext.accounts[0].localAccountId;
        throw new Error("User is not logged in to designer");
    }

    function getAttributeFromMap(attributeId) {
        const entityIds = Object.keys(entityAttributeMapRef.current);
        for (const entityId of entityIds) {
            const attribute = entityAttributeMapRef.current[entityId].find(attribute => attribute._id === attributeId);
            if (attribute) {
                return attribute;
            }
        }
        return null;
    }

    async function handlePublishFromButton() {
        try {
            await performIncrementalGeneration();
            setMessages((prev) => [...prev, {
                role: "system",
                content: "Application published successfully."
            }]);
        } catch (e) {
            console.error(e);
        }
    }

    function handleExpandChange(event) {
        setTreeOptions(options => options.map(option => {
            if (option.id === event.item.id) {
                return {
                    ...option,
                    isExpanded: !option.isExpanded
                }
            }
            return option;
        }));
    }

    function handleSubTreeExpandChange(event) {
        setSubTreeOptions(options => options.map(option => {
            if (option.id === event.item.id) {
                return {
                    ...option,
                    isExpanded: !option.isExpanded
                }
            }
            return option;
        }));
    }

    async function changeContext(properties) {
        const { tab, entityId, attributeId, entityRelationId, roleId, portalId, pageId } = properties;
        if (attributeId || pageId) {
            if (entityId) {
                const entity = allEntities.find(entity => entity._id === entityId);
                if (!entity) {
                    throw new Error("Entity with id " + entityId + " does not exist.");
                }
            } else {
                throw new Error("Entity id is required.");
            }
        }
        let message = "";
        if (entityId) {
            if (attributeId) {
                const entity = allEntities.find(entity => entity._id === entityId);
                const attributes = await getAttributes(entityId);
                const attribute = attributes.find(attribute => attribute._id === attributeId);
                if (!attribute) {
                    throw new Error("Attribute with id " + attributeId + " does not exist.");
                }
                handleChange({
                    value: {
                        id: entity._id,
                        name: entity.name
                    }
                })
                setSubTreeValue({
                    id: attribute._id,
                    name: attribute.attributeName
                })
                // handleSubTreeChange({
                //     value: {
                //         id: attribute._id,
                //         name: attribute.attributeName
                //     }
                // })
                message = `
                    Now the user is at the entity ${entity.name} and the current attribute selected by the user is ${attribute.attributeName}
                    The details of the selected entity is: ${JSON.stringify(entity)}
                    The details of the selected attribute is: ${JSON.stringify(attribute)}
                `
            } else if (pageId) {
                const entity = allEntities.find(entity => entity._id === entityId);
                const pages = await getEntityPages(entityId);
                const page = pages.data.find(page => page._id === pageId);
                if (!page) {
                    throw new Error("Page with id " + pageId + " does not exist.");
                }
                handleChange({
                    value: {
                        id: entity._id,
                        name: entity.name
                    }
                })
                handleSubTreeChange({
                    value: {
                        id: page._id,
                        name: page.pageName
                    }
                })
                const { component, ...pageProperties } = page;
                message = `
                    Now the user is at the entity ${entity.name} and the current page selected by the user is ${page.pageName}
                    The details of the selected entity is: ${JSON.stringify(entity)}
                    The details of the selected page is: ${JSON.stringify(pageProperties)}
                `;
            } else if (!attributeId && !pageId) {
                const entities = await getEntities(projectId);
                const entity = entities.find(entity => entity._id === entityId);
                handleChange({
                    value: {
                        id: entity._id,
                        name: entity.name
                    }
                })
                setSubTreeValue(null);
                message = `
                    Now the user is at the entity ${entity.name}
                    The details of the selected entity is: ${JSON.stringify(entity)}
                `;
            }
        } else if (entityRelationId) {
            const relations = await getProjectRelations(projectId);
            const relation = relations.find(relation => relation._id === entityRelationId);
            if (!relation) {
                throw new Error("Relation with id " + entityRelationId + " does not exist.");
            }
            handleChange({
                value: {
                    id: relation._id,
                    name: relation.identifier
                }
            })
            setSubTreeValue(null);
            message = `
                Now the user is at the relation ${relation.identifier}
                The details of the selected relation is: ${JSON.stringify(relation)}
            `;
        } else if (roleId) {
            const roles = await getProjectRoles(projectId);
            const role = roles.find(role => role._id === roleId);
            if (!role) {
                throw new Error("Role with id " + roleId + " does not exist.");
            }
            handleChange({
                value: {
                    id: role._id,
                    name: role.roleName
                }
            })
            setSubTreeValue(null);
            message = `
                Now the user is at the role ${role.roleName}
                The details of the selected role is: ${JSON.stringify(role)}
            `;
        } else if (portalId) {
            const portals = await getPortals(projectId);
            const portal = portals.find(portal => portal._id === portalId);
            if (!portal) {
                throw new Error("Portal with id " + portalId + " does not exist.");
            }
            handleChange({
                value: {
                    id: portal._id,
                    name: portal.portalName
                }
            })
            setSubTreeValue(null);
            message = `
                Now the user is at the portal ${portal.portalName}
                The details of the selected portal is: ${JSON.stringify(portal)}
            `;
        } else if (!entityId && !roleId && !portalId) {
            const tab = treeOptions.find(option => option.name === properties.tab);
            setTreeValue(tab);
            setSubTreeValue(null);
            message = `Now the user is at the tab ${tab.name}`;
        }
        return message.trim()
    }

    async function handleChange(event, subTreeValue, options = { sendMessage: true }) {

        let topLevelRoot = treeOptions.find(option => option.id === event.value.id);
        let children = null;
        // handle children selection
        debugger;
        if (topLevelRoot === undefined) {
            for (const option of treeOptions) {
                const childrenIndex = option.children.findIndex(child => child.id === event.value.id);
                const isChildren = childrenIndex > -1;
                if (isChildren) {
                    topLevelRoot = option;
                    children = option.children[childrenIndex];
                    break;
                }
            }
            if (options.sendMessage) {
                // change current 'this' value to children + top level
                const msgEvent = {
                    type: "conversation.item.create",
                    item: {
                        type: "message",
                        role: "system",
                        content: [
                            {
                                type: "input_text",
                                text: `
                                    Now the user has selected the ${topLevelRoot.name} tab and the current ${topLevelRoot.name} selected by the user is ${event.value.name}. The id of the selected ${topLevelRoot.name} is ${event.value.id}
                                `.trim(),
                            },
                        ],
                    }
                }
                sendClientEvent(msgEvent);
            }
            setMessages((prev) => [...prev, {
                role: "info",
                content: event.value.name + " " + topLevelRoot.name + " is now selected."
            }]);
        } else {
            // Change current 'this' value to selected top level root.
            if (options.sendMessage) {
                const event = {
                    type: "conversation.item.create",
                    item: {
                        type: "message",
                        role: "system",
                        content: [
                            {
                                type: "input_text",
                                text: "Now the user is at " + topLevelRoot.name + " root level. Help user with the operations that can be done on the " + topLevelRoot.name + " level."
                            }
                        ]
                    }
                }
                sendClientEvent(event);
            }
            setMessages((prev) => [...prev, {
                role: "info",
                content: topLevelRoot.name + " is now selected."
            }]);
        }
        if (topLevelRoot.name === "Entities" && children !== null) {
            const attributes = await getAttributes(children.id);
            const pages = await getEntityPages(children.id);
            setSubTreeOptions([
                {
                    id: 1,
                    name: "Attributes",
                    isExpanded: false,
                    children: attributes.map(attribute => ({
                        id: attribute._id,
                        name: attribute.attributeName
                    }))
                },
                {
                    id: 2,
                    name: "Pages",
                    isExpanded: false,
                    children: pages.data.map(page => ({
                        id: page._id,
                        name: page.pageName
                    }))
                }
            ])
            if (subTreeValue && subTreeValue.id) {
                const attribute = attributes.find(attribute => attribute._id === subTreeValue.id);
                if (attribute) {
                    setSubTreeValue({
                        id: attribute._id,
                        name: attribute.attributeName
                    });
                } else {
                    const page = pages.data.find(page => page._id === subTreeValue.id);
                    if (page) {
                        setSubTreeValue({
                            id: page._id,
                            name: page.pageName
                        });
                    }
                }
            }
        } else {
            setSubTreeOptions([
                {
                    id: 1,
                    name: "Entities",
                    isExpanded: true,
                    children: allEntities.map(entity => ({
                        id: entity._id,
                        name: entity.name
                    }))
                }
            ]);
        }
        setTreeValue(event.value);
    }

    function handleSubTreeChange(event) {
        setSubTreeValue(event.value);
        let treeTopLevelRoot = treeOptions.find(option => option.id === treeValue.id);
        let treeChildren = null;
        if (treeTopLevelRoot === undefined) {
            for (const option of treeOptions) {
                const childrenIndex = option.children.findIndex(child => child.id === treeValue.id);
                const isChildren = childrenIndex > -1;
                if (isChildren) {
                    treeTopLevelRoot = option;
                    treeChildren = option.children[childrenIndex];
                    break;
                }
            }
        }
        let topLevelRoot = subTreeOptions.find(option => option.id === event.value.id);
        let children = null;
        if (topLevelRoot === undefined) {
            for (const option of subTreeOptions) {
                const childrenIndex = option.children.findIndex(child => child.id === event.value.id);
                const isChildren = childrenIndex > -1;
                if (isChildren) {
                    topLevelRoot = option;
                    children = option.children[childrenIndex];
                    break;
                }
            }
        }
        const msgEvent = {
            type: "conversation.item.create",
            item: {
                type: "message",
                role: "system",
                content: [
                    {
                        type: "input_text",
                        text: `
                            Inside the current selected ${treeTopLevelRoot.name}, the user has selected the ${topLevelRoot.name} and the current ${topLevelRoot.name} selected by the user is ${event.value.name}. The selected element id is: ${event.value.id}
                        `.trim(),
                    },
                ],
            },
        }
        sendClientEvent(msgEvent);
        setMessages((prev) => [...prev, {
            role: "info",
            content: event.value.name + " is now selected."
        }]);
    }

    function toggleMessageToolCalls(eventId) {
        setMessages(messages => messages.map(message => {
            if (message.eventId === eventId) {
                return {
                    ...message,
                    showToolCalls: !message.showToolCalls
                }
            }
            return message;
        }))
    }

    return (
        <div className="d-flex flex-column w-100 h-100 p-2">
            <div className="w-100 d-flex justify-content-between align-items-end pb-2 border-bottom">
                <div>
                    <div>
                        <b>Set context to: </b>
                    </div>
                    <div>
                        <DropDownTree
                            className="border"
                            style={{ width: "300px" }}
                            data={treeOptions}
                            value={treeValue}
                            textField="name"
                            expandField="isExpanded"
                            subItemsField="children"
                            selectField="isSelected"
                            onExpandChange={handleExpandChange}
                            onChange={handleChange}
                        />
                        &nbsp;
                        {
                            treeValue &&
                            treeValue.type !== "portal" &&
                            treeValue.name !== "Portals" &&
                            <DropDownTree
                                className="border"
                                style={{ width: "250px" }}
                                data={subTreeOptions}
                                value={subTreeValue}
                                textField="name"
                                expandField="isExpanded"
                                subItemsField="children"
                                selectField="isSelected"
                                onExpandChange={handleSubTreeExpandChange}
                                onChange={handleSubTreeChange}
                            />
                        }
                    </div>
                </div>
                <div>
                    <button className="btn btn-primary" onClick={handlePublishFromButton}>
                        Publish
                    </button>
                </div>
            </div>
            <div className="chat-body">
                {
                    messages.map((message, index) => {
                        switch (message.role) {
                            case "user":
                                return (
                                    <div className="message-row">
                                        <div key={index} className="chat-bubble-right">
                                            <div>
                                                {message.content}
                                            </div>
                                            {
                                                message.toolCalls && message.toolCalls.length > 0 &&
                                                <button
                                                    className="btn p-0 border-0 align-self-end text-link"
                                                    onClick={() => toggleMessageToolCalls(message.eventId)}
                                                >
                                                    Show tool calls
                                                </button>
                                            }
                                            {
                                                message.showToolCalls &&
                                                <Dialog
                                                    title={"Tool calls made"}
                                                    onClose={() => toggleMessageToolCalls(message.eventId)}
                                                    width={200}
                                                    height={500}
                                                >
                                                    <Scrollable>
                                                        <ol>
                                                            {
                                                                message.toolCalls.map((toolCall, index) => (
                                                                    <li>
                                                                        <pre key={index}>
                                                                            {JSON.stringify(toolCall, null, 2)}
                                                                        </pre>
                                                                    </li>
                                                                ))
                                                            }
                                                        </ol>
                                                    </Scrollable>
                                                </Dialog>
                                            }
                                        </div>
                                    </div>
                                )
                            case "info":
                                return (
                                    <div key={index} className="message-row system-message-row">
                                        <div className="chat-bubble-system">
                                            {message.content}
                                        </div>
                                    </div>
                                )
                            case "loader":
                                return (
                                    <div key={index} className="message-row">
                                        <div className="chat-bubble-left">
                                            <Loader />
                                        </div>
                                    </div>
                                )
                            default:
                                return (
                                    <div key={index} className="message-row">
                                        <div className="chat-bubble-left">
                                            <Markdown>
                                                {message.content}
                                            </Markdown>
                                        </div>
                                    </div>
                                )
                        }
                    })
                }
            </div>
            <div className="chat-footer d-flex">
                <input
                    autoFocus
                    className="form-control w-90"
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === "Enter") {
                            sendTextMessage();
                            setMessage("");
                        }
                    }}
                />
                <button
                    className="btn btn-primary"
                    onClick={sendTextMessage}
                >
                    Send
                </button>
            </div>
        </div>
    )

}

export default RealtimeAPIChatbox;